import { useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Upload, Crosshair, RefreshCw, Download, Save, Image as ImageIcon,
  Trash2, ZoomIn, ZoomOut, Maximize2, Settings2, Loader2, Layers, Eye,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : `${BACKEND}${u.startsWith("/") ? "" : "/"}${u}`);

const fileToDataUri = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export default function AdminTruthPrint() {
  // Data
  const [templates, setTemplates] = useState([]);
  const [artworks, setArtworks] = useState([]);

  // Selection
  const [sourceKind, setSourceKind] = useState("artwork");   // artwork | upload
  const [artworkId, setArtworkId] = useState("");
  const [uploadImageUrl, setUploadImageUrl] = useState("");   // stored URL (small), set after upload
  const [uploadFilename, setUploadFilename] = useState("");
  const [uploading, setUploading] = useState(false);

  const [templateKey, setTemplateKey] = useState("11x14");

  // Placement
  const [scale, setScale] = useState(1.0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [scaleMode, setScaleMode] = useState("fill");
  const [jpgQuality, setJpgQuality] = useState(95);
  const [exports, setExports] = useState(["png"]);   // which file types to produce
  const [trimSource, setTrimSource] = useState(false);

  // Element overrides
  const [showLogo, setShowLogo] = useState(true);
  const [showBorder, setShowBorder] = useState(true);
  const [showTitle, setShowTitle] = useState(false);
  const [showWebsite, setShowWebsite] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [title, setTitle] = useState("");
  const [website, setWebsite] = useState("SIMPLETRUTHSCLUB.COM");
  const [qrUrl, setQrUrl] = useState("https://simpletruthsclub.com");

  // Guides
  const [gSafe, setGSafe] = useState(true);
  const [gBorder, setGBorder] = useState(true);
  const [gLogo, setGLogo] = useState(true);
  const [gTrim, setGTrim] = useState(true);

  // UI
  const [previewBlob, setPreviewBlob] = useState("");
  const [previewBust, setPreviewBust] = useState(0);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [tplEditorOpen, setTplEditorOpen] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobStatus, setJobStatus] = useState("");
  const [zoom, setZoom] = useState(1);

  const tpl = useMemo(() => templates.find((t) => t.key === templateKey), [templates, templateKey]);
  const art = useMemo(() => artworks.find((a) => a.id === artworkId), [artworks, artworkId]);

  // Init defaults from template when it changes
  useEffect(() => {
    if (!tpl) return;
    setShowLogo(!!tpl.show_logo);
    setShowTitle(!!tpl.show_title);
    setShowWebsite(!!tpl.show_website);
    setShowQR(!!tpl.show_qr);
    setScaleMode(tpl.scale_mode || "fill");
    setJpgQuality(Number(tpl.jpg_quality) || 95);
    setExports(Array.isArray(tpl.exports) && tpl.exports.length ? tpl.exports : ["png"]);
    setTrimSource(!!tpl.trim_source);
  }, [tpl?.key]); // eslint-disable-line

  // Reset placement when source changes
  useEffect(() => { setScale(1); setOffsetX(0); setOffsetY(0); }, [artworkId, uploadImageUrl, sourceKind]);

  const load = async () => {
    const [tps, ars] = await Promise.all([
      http.get("/admin/print/templates").catch(() => ({ data: [] })),
      http.get("/artworks?status_filter=active&limit=500").catch(() => ({ data: [] })),
    ]);
    setTemplates(tps.data || []);
    setArtworks(ars.data || []);
  };
  useEffect(() => { load(); }, []);

  const sourceReady = (sourceKind === "artwork" && !!artworkId) ||
                       (sourceKind === "upload" && !!uploadImageUrl);

  // Live preview — debounced
  useEffect(() => {
    if (!sourceReady || !tpl) { setPreviewBlob(""); return; }
    let aborted = false;
    const id = setTimeout(async () => {
      setBusy(true);
      try {
        const body = {
          template_key: templateKey,
          source: sourceKind === "artwork"
            ? { kind: "artwork", artwork_id: artworkId }
            : { kind: "upload", image_url: uploadImageUrl },
          scale, offset_x: offsetX, offset_y: offsetY,
          scale_mode: scaleMode,
          trim_source: trimSource,
          show_logo: showLogo, show_border: showBorder, show_title: showTitle,
          show_website: showWebsite, show_qr: showQR,
          title: showTitle ? title : "",
          website: showWebsite ? website : "",
          qr_url: showQR ? qrUrl : "",
          guide_safe: gSafe, guide_border: gBorder,
          guide_logo: gLogo, guide_trim: gTrim,
        };
        const { data } = await http.post("/admin/print/preview", body, { responseType: "blob" });
        if (aborted) return;
        if (previewBlob) URL.revokeObjectURL(previewBlob);
        setPreviewBlob(URL.createObjectURL(data));
      } catch (e) {
        toast.error("Preview failed: " + (e.response?.data?.detail || e.message));
      } finally {
        if (!aborted) setBusy(false);
      }
    }, 280);
    return () => { aborted = true; clearTimeout(id); };
    // eslint-disable-next-line
  }, [templateKey, artworkId, uploadImageUrl, sourceKind, scale, offsetX, offsetY,
      scaleMode, trimSource, showLogo, showBorder, showTitle, showWebsite, showQR, title, website, qrUrl,
      gSafe, gBorder, gLogo, gTrim, previewBust]);

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { toast.error("Image must be ≤ 30 MB"); return; }
    setUploading(true);
    setUploadImageUrl("");
    try {
      const dataUri = await fileToDataUri(file);
      // Upload ONCE here; subsequent preview/generate calls reference the URL.
      const { data } = await http.post("/upload/image", { data_uri: dataUri, filename: file.name });
      setUploadImageUrl(data.url);
      setUploadFilename(file.name);
      toast.success(`Uploaded ${file.name}.`);
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const autoCenter = () => { setOffsetX(0); setOffsetY(0); setScale(1); toast.success("Centered."); };
  const autoFill = () => { setScaleMode("fill"); setScale(1); setOffsetX(0); setOffsetY(0); toast.success("Auto-filled (covers artwork area)."); };
  const autoFit = () => { setScaleMode("fit"); setScale(1); setOffsetX(0); setOffsetY(0); toast.success("Fit (entire artwork visible)."); };

  const generate = async () => {
    if (!sourceReady) { toast.error("Pick an artwork or upload an image first."); return; }
    setGenerating(true);
    setJobProgress(0);
    setJobStatus("pending");
    setJobId(null);
    setLastGenerated(null);
    try {
      const body = {
        template_key: templateKey,
        source: sourceKind === "artwork"
          ? { kind: "artwork", artwork_id: artworkId }
          : { kind: "upload", image_url: uploadImageUrl },
        scale, offset_x: offsetX, offset_y: offsetY,
        scale_mode: scaleMode,
        trim_source: trimSource,
        jpg_quality: jpgQuality,
        exports,
        show_logo: showLogo, show_border: showBorder, show_title: showTitle,
        show_website: showWebsite, show_qr: showQR,
        title, website, qr_url: qrUrl,
      };
      const { data } = await http.post("/admin/print/generate", body);
      setJobId(data.job_id);
      setJobStatus(data.status || "pending");
      // Polling happens via effect below
    } catch (e) {
      toast.error("Generate failed: " + (e.response?.data?.detail || e.message));
      setGenerating(false);
      setJobStatus("");
    }
  };

  // Poll job status every 3 seconds until completed/failed
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await http.get(`/admin/print/generate/${jobId}`);
        if (cancelled) return;
        setJobStatus(data.status || "");
        setJobProgress(Number(data.progress) || 0);
        if (data.status === "completed") {
          setLastGenerated(data.result || null);
          setGenerating(false);
          setJobId(null);
          toast.success("Print files generated.");
        } else if (data.status === "failed") {
          toast.error("Generate failed: " + (data.error || "unknown error"));
          setGenerating(false);
          setJobId(null);
        }
      } catch (e) {
        toast.error("Status poll failed: " + (e.response?.data?.detail || e.message));
        setGenerating(false);
        setJobId(null);
      }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [jobId]);

  const batchGenerate = async () => {
    if (!window.confirm("Generate all MISSING print files for every active artwork? This may take a while. It will run in the background.")) return;
    setGenerating(true);
    try {
      const { data } = await http.post("/admin/print/batch/generate-missing");
      toast.success(data.message || "Batch generation started.");
    } catch (e) {
      toast.error("Batch failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  // Logo upload
  const logoInputRef = useRef();
  const replaceLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const uri = await fileToDataUri(file);
    try {
      await http.post("/admin/print/logo", { data_uri: uri });
      toast.success("Custom print logo saved.");
      setPreviewBust(previewBust + 1);
    } catch { toast.error("Logo upload failed."); }
  };
  const resetLogo = async () => {
    try {
      await http.delete("/admin/print/logo");
      toast.success("Reverted to default STC seal.");
      setPreviewBust(previewBust + 1);
    } catch { toast.error("Reset failed."); }
  };

  return (
    <div className="space-y-8" data-testid="admin-truth-print">
      <div>
        <h2 className="font-display text-2xl text-[#F4F1EA] mb-2 flex items-center gap-2">
          <Sparkles size={18} className="text-[#C9A055]" /> Truth Print Builder
        </h2>
        <p className="font-body text-[13px] text-[#8a857d] max-w-3xl">
          Generate final, print-ready PNG + JPG + Web Preview files for every Simple Truths Club artwork.
          Aspect ratio is always preserved — no stretching, skewing, or distortion. The original artwork file is never modified.
        </p>
      </div>

      <div className="grid xl:grid-cols-[420px_1fr_360px] gap-6">
        {/* LEFT — source + template */}
        <div className="space-y-5">
          {/* Source */}
          <div className="bg-[#0a0a0a] border border-white/10 p-5">
            <Label>Source artwork</Label>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setSourceKind("artwork")} data-testid="src-artwork"
                      className={`flex-1 btn-outline text-[11px] ${sourceKind === "artwork" ? "border-[#C9A055] text-[#C9A055]" : ""}`}>
                Pick existing
              </button>
              <button onClick={() => setSourceKind("upload")} data-testid="src-upload"
                      className={`flex-1 btn-outline text-[11px] ${sourceKind === "upload" ? "border-[#C9A055] text-[#C9A055]" : ""}`}>
                Upload new
              </button>
            </div>
            {sourceKind === "artwork" ? (
              <select value={artworkId} onChange={(e) => setArtworkId(e.target.value)}
                      data-testid="src-artwork-select" className="stc-input">
                <option value="">— select an artwork —</option>
                {artworks.map((a) => (
                  <option key={a.id} value={a.id}>{a.title}</option>
                ))}
              </select>
            ) : (
              <div className="space-y-2">
                <label className="btn-outline w-full text-center cursor-pointer text-[11px] flex items-center justify-center gap-2">
                  {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Upload size={12} /> Choose image</>}
                  <input type="file" accept="image/*" className="hidden" onChange={onUploadFile} data-testid="src-upload-file" disabled={uploading} />
                </label>
                {uploadImageUrl && !uploading && (
                  <div className="text-[10px] text-[#C9A055]">
                    ✓ {uploadFilename || "Uploaded"} — replace by choosing another.
                  </div>
                )}
              </div>
            )}
            {art?.print_assets?.[templateKey] && (
              <div className="mt-3 text-[10px] text-[#C9A055]">
                ✓ This artwork already has a {templateKey} print on file.
              </div>
            )}
          </div>

          {/* Template */}
          <div className="bg-[#0a0a0a] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <Label className="!mb-0">Template</Label>
              <button onClick={() => setTplEditorOpen(!tplEditorOpen)} className="text-[#8a857d] hover:text-[#C9A055]" data-testid="tpl-editor-toggle">
                <Settings2 size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {templates.map((t) => (
                <button key={t.key} onClick={() => setTemplateKey(t.key)} data-testid={`tpl-${t.key}`}
                        className={`btn-outline text-[11px] ${templateKey === t.key ? "border-[#C9A055] text-[#C9A055]" : ""}`}>
                  {t.label}
                </button>
              ))}
            </div>
            {tpl && (
              <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">
                Canvas {tpl.canvas_w}×{tpl.canvas_h} · Art {tpl.canvas_w - tpl.border_left - tpl.border_right}×{tpl.canvas_h - tpl.border_top - tpl.border_bottom}
              </div>
            )}
            {tplEditorOpen && tpl && (
              <TemplateEditor tpl={tpl} onSaved={(t) => { setTemplates(templates.map((x) => x.key === t.key ? t : x)); }} />
            )}
          </div>

          {/* Logo source */}
          <div className="bg-[#0a0a0a] border border-white/10 p-5">
            <Label>Print logo</Label>
            <div className="flex gap-2">
              <label className="btn-outline text-[11px] flex-1 text-center cursor-pointer flex items-center justify-center gap-1.5">
                <ImageIcon size={12} /> Replace
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={replaceLogo} data-testid="logo-replace" />
              </label>
              <button onClick={resetLogo} className="btn-outline text-[11px] flex items-center gap-1" data-testid="logo-reset">
                <Trash2 size={12} /> Default
              </button>
            </div>
            <p className="text-[10px] text-[#8a857d] mt-2">Used as the bottom mark on all generated prints. Always horizontally centered.</p>
          </div>
        </div>

        {/* CENTER — preview */}
        <div className="bg-[#0a0a0a] border border-white/10 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <Label className="!mb-0">Live Preview</Label>
            <div className="flex items-center gap-2">
              <button onClick={() => setZoom(Math.max(0.25, zoom - 0.15))} className="text-[#8a857d] hover:text-[#C9A055]"><ZoomOut size={14} /></button>
              <button onClick={() => setZoom(1)} className="text-[#8a857d] hover:text-[#C9A055]" title="Fit"><Maximize2 size={14} /></button>
              <button onClick={() => setZoom(zoom + 0.15)} className="text-[#8a857d] hover:text-[#C9A055]"><ZoomIn size={14} /></button>
              <button onClick={() => setPreviewBust(previewBust + 1)} className="text-[#8a857d] hover:text-[#C9A055]" title="Refresh preview" data-testid="tp-refresh">
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-[420px] bg-[#111] border border-white/5 flex items-center justify-center relative overflow-auto">
            {busy && (
              <div className="absolute top-2 right-2 z-10 bg-black/70 px-2 py-1 text-[10px] text-[#C9A055] flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Rendering…
              </div>
            )}
            {previewBlob ? (
              <img src={previewBlob} alt="preview" data-testid="tp-preview-img"
                   style={{ transform: `scale(${zoom})`, transformOrigin: "center" }}
                   className="max-w-full max-h-[80vh] object-contain transition-transform" />
            ) : (
              <div className="text-[12px] text-[#8a857d] p-6 text-center">
                {sourceReady ? "Loading preview…" : "Pick an artwork or upload a file to begin."}
              </div>
            )}
          </div>

          {/* Guide toggles */}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">
            <span className="flex items-center gap-1.5"><Layers size={11} /> Overlays:</span>
            <ToggleChip label="Trim" color="#dc3c3c" on={gTrim} onChange={setGTrim} testid="g-trim" />
            <ToggleChip label="Border" color="#f0b41e" on={gBorder} onChange={setGBorder} testid="g-border" />
            <ToggleChip label="Safe area" color="#5cc070" on={gSafe} onChange={setGSafe} testid="g-safe" />
            <ToggleChip label="Logo zone" color="#6890e6" on={gLogo} onChange={setGLogo} testid="g-logo" />
          </div>
        </div>

        {/* RIGHT — placement + elements + actions */}
        <div className="space-y-5">
          {/* Placement */}
          <div className="bg-[#0a0a0a] border border-white/10 p-5">
            <Label>Scaling Mode</Label>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setScaleMode("fill")} data-testid="sm-fill"
                      className={`flex-1 btn-outline text-[11px] ${scaleMode === "fill" ? "border-[#C9A055] text-[#C9A055]" : ""}`}>
                FILL <span className="opacity-60 ml-1">(crop overflow)</span>
              </button>
              <button onClick={() => setScaleMode("fit")} data-testid="sm-fit"
                      className={`flex-1 btn-outline text-[11px] ${scaleMode === "fit" ? "border-[#C9A055] text-[#C9A055]" : ""}`}>
                FIT <span className="opacity-60 ml-1">(no crop)</span>
              </button>
            </div>
            <div className="font-body text-[11px] text-[#8a857d] mb-4">
              {scaleMode === "fill"
                ? "Artwork covers the whole artwork area. Excess on the long edges is cropped equally from both sides. Aspect ratio preserved."
                : "Entire artwork remains visible inside the artwork area. No cropping. Aspect ratio preserved."}
            </div>

            <div className="flex items-center justify-between mb-4 pt-3 border-t border-white/5">
              <div>
                <div className="font-ui text-[11px] text-[#F4F1EA]">Auto-trim source whitespace</div>
                <div className="font-body text-[10px] text-[#8a857d] mt-0.5">
                  Crops built-in white margins from the source before placement.
                </div>
              </div>
              <button type="button" onClick={() => setTrimSource(!trimSource)} data-testid="tp-trim"
                      className={`w-9 h-5 rounded-full transition relative ${trimSource ? "bg-[#C9A055]" : "bg-white/15"}`}>
                <span className={`absolute top-0.5 ${trimSource ? "left-4" : "left-0.5"} w-4 h-4 rounded-full bg-white transition-all`} />
              </button>
            </div>

            <Label>Placement</Label>
            <SliderRow label="Scale" value={scale} min={0.5} max={1.5} step={0.01} suffix="x" onChange={setScale} testid="tp-scale" />
            <SliderRow label="Offset X" value={offsetX} min={-1500} max={1500} step={5} suffix="px" onChange={setOffsetX} testid="tp-ox" />
            <SliderRow label="Offset Y" value={offsetY} min={-1500} max={1500} step={5} suffix="px" onChange={setOffsetY} testid="tp-oy" />
            <div className="grid grid-cols-3 gap-2 mt-2">
              <button onClick={autoFill} data-testid="tp-autofill" className="btn-outline text-[11px] flex items-center justify-center gap-1">
                Auto Fill
              </button>
              <button onClick={autoFit} data-testid="tp-autofit" className="btn-outline text-[11px] flex items-center justify-center gap-1">
                Auto Fit
              </button>
              <button onClick={autoCenter} data-testid="tp-autocenter" className="btn-outline text-[11px] flex items-center justify-center gap-1">
                <Crosshair size={11} /> Center
              </button>
            </div>
          </div>

          {/* Elements */}
          <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
            <Label>Elements</Label>
            <CheckRow label="Border (white margin)" on={showBorder} onChange={setShowBorder} testid="el-border" />
            <CheckRow label="Logo (always centered)" on={showLogo} onChange={setShowLogo} testid="el-logo" />
            <CheckRow label="Title" on={showTitle} onChange={setShowTitle} testid="el-title" />
            {showTitle && (
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder={art?.title || "Artwork title"} className="stc-input" data-testid="el-title-input" />
            )}
            <CheckRow label="Website" on={showWebsite} onChange={setShowWebsite} testid="el-website" />
            {showWebsite && (
              <input value={website} onChange={(e) => setWebsite(e.target.value)}
                     className="stc-input" data-testid="el-website-input" />
            )}
            <CheckRow label="QR code" on={showQR} onChange={setShowQR} testid="el-qr" />
            {showQR && (
              <input value={qrUrl} onChange={(e) => setQrUrl(e.target.value)}
                     placeholder="https://…" className="stc-input" data-testid="el-qr-input" />
            )}
          </div>

          {/* Actions */}
          <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
            <div>
              <Label>Export Formats</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "png", label: "PNG (lossless)" },
                  { k: "jpg", label: "JPG" },
                  { k: "web", label: "Web preview" },
                ].map((f) => {
                  const on = exports.includes(f.k);
                  return (
                    <button
                      key={f.k}
                      type="button"
                      data-testid={`exp-${f.k}`}
                      onClick={() => {
                        if (on) {
                          if (exports.length === 1) { toast.error("At least one export is required."); return; }
                          setExports(exports.filter((x) => x !== f.k));
                        } else {
                          setExports([...exports, f.k]);
                        }
                      }}
                      className={`px-2 py-1 border text-[10px] tracking-[0.18em] uppercase transition ${on ? "border-[#C9A055] text-[#F4F1EA]" : "border-white/15 text-[#8a857d]"}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
              <p className="font-body text-[11px] text-[#8a857d] mt-2">
                Toggle which files are produced. Fewer formats = faster + less memory.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/5">
              <Label className="!mb-0">JPG Quality</Label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={jpgQuality}
                  onChange={(e) => setJpgQuality(Math.max(1, Math.min(100, Number(e.target.value) || 95)))}
                  data-testid="tp-jpg-quality"
                  disabled={!exports.includes("jpg")}
                  className="stc-input !w-20 !py-1.5 text-center disabled:opacity-50"
                />
                <span className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">/ 100</span>
              </div>
            </div>
            <p className="font-body text-[11px] text-[#8a857d] -mt-1">
              Default 95. Only used when JPG is enabled.
            </p>
            <button onClick={generate} disabled={!sourceReady || generating}
                    data-testid="tp-generate"
                    className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {generating ? `${jobStatus === "running" ? "Rendering" : "Queued"}… ${jobProgress}%` : `Generate ${templateKey} files`}
            </button>
            {generating && (
              <div className="w-full h-1.5 bg-white/10 overflow-hidden" data-testid="tp-job-bar">
                <div className="h-full bg-[#C9A055] transition-all duration-300"
                     style={{ width: `${Math.max(2, jobProgress)}%` }} />
              </div>
            )}
            <button onClick={batchGenerate} disabled={generating}
                    data-testid="tp-batch"
                    className="btn-outline w-full flex items-center justify-center gap-2 disabled:opacity-50">
              <Sparkles size={12} /> Generate ALL missing files
            </button>
          </div>

          {lastGenerated && (
            <div className="bg-[#0a0a0a] border border-[#C9A055]/30 p-4">
              <Label>Last generated</Label>
              <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-2">
                {lastGenerated.template_key} · {lastGenerated.canvas_w}×{lastGenerated.canvas_h}
                {lastGenerated.jpg_quality ? ` · jpg q${lastGenerated.jpg_quality}` : ""}
              </div>
              <div className="flex flex-wrap gap-2">
                {lastGenerated.png_url && (
                  <a href={abs(lastGenerated.png_url)} target="_blank" rel="noreferrer" download
                     data-testid="lg-png"
                     className="btn-outline text-[10px] flex items-center gap-1"><Download size={11} /> PNG</a>
                )}
                {lastGenerated.jpg_url && (
                  <a href={abs(lastGenerated.jpg_url)} target="_blank" rel="noreferrer" download
                     data-testid="lg-jpg"
                     className="btn-outline text-[10px] flex items-center gap-1"><Download size={11} /> JPG</a>
                )}
                {lastGenerated.web_url && (
                  <a href={abs(lastGenerated.web_url)} target="_blank" rel="noreferrer" download
                     data-testid="lg-web"
                     className="btn-outline text-[10px] flex items-center gap-1"><Download size={11} /> Web</a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PrintFilesManager refreshKey={lastGenerated ? lastGenerated.generated_at : "init"} templates={templates} />
    </div>
  );
}

// =====================================================================
// File manager — list / view / delete saved print files
// =====================================================================
function PrintFilesManager({ refreshKey, templates }) {
  const [files, setFiles] = useState([]);
  const [filterTpl, setFilterTpl] = useState("");
  const [filterExt, setFilterExt] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTpl) params.set("template_key", filterTpl);
      if (filterExt) params.set("ext", filterExt);
      params.set("limit", "300");
      const { data } = await http.get(`/admin/print/files?${params.toString()}`);
      setFiles(data || []);
    } catch (e) {
      toast.error("Failed to load files.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshKey, filterTpl, filterExt]); // eslint-disable-line

  const remove = async (fid) => {
    if (!window.confirm("Delete this print file permanently? It will also be unlinked from its artwork's print_assets.")) return;
    try {
      await http.delete(`/admin/print/files/${fid}`);
      setFiles(files.filter((f) => f.id !== fid));
      toast.success("File deleted.");
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const removeAll = async () => {
    if (!window.confirm(`Delete ALL files matching current filters? (${files.length} files)`)) return;
    try {
      const params = new URLSearchParams();
      if (filterTpl) params.set("template_key", filterTpl);
      if (filterExt) params.set("ext", filterExt);
      await http.post(`/admin/print/files/bulk-delete?${params.toString()}`);
      toast.success("Bulk delete completed.");
      load();
    } catch (e) {
      toast.error("Bulk delete failed.");
    }
  };

  const downloadZip = () => {
    if (files.length === 0) return;
    const params = new URLSearchParams();
    if (filterTpl) params.set("template_key", filterTpl);
    if (filterExt) params.set("ext", filterExt);
    
    // Add token for auth
    const token = localStorage.getItem("stc_token");
    if (token) params.set("token", token);
    
    // Redirect to download endpoint
    // Wait, the backend requires Bearer token usually. The backend relies on Depends(require_admin).
    // It reads token from header. A direct browser download won't send header.
    // Let's add token in query param support for this endpoint in server.py if needed, 
    // or just fetch as blob and trigger download via JS.
  };

  const downloadZipBlob = async () => {
    if (files.length === 0) return;
    setLoading(true);
    toast.info("Generating ZIP file... this may take a minute.");
    try {
      const params = new URLSearchParams();
      if (filterTpl) params.set("template_key", filterTpl);
      if (filterExt) params.set("ext", filterExt);
      
      const { data } = await http.get(`/admin/print/files/download-zip?${params.toString()}`, {
        responseType: 'blob',
        timeout: 120000 // 2 minutes for zip gen
      });
      
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stc_print_files_${filterTpl || 'all'}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started!");
    } catch (e) {
      toast.error("ZIP download failed.");
    } finally {
      setLoading(false);
    }
  };

  const totalBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);
  const human = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-5" data-testid="pf-manager">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-display text-xl text-[#F4F1EA]">Saved Print Files</h3>
          <p className="font-body text-[12px] text-[#8a857d] mt-1">
            {files.length} files · {human(totalBytes)} total
          </p>
        </div>
        <div>
          <Label>Template</Label>
          <select value={filterTpl} onChange={(e) => setFilterTpl(e.target.value)}
                  data-testid="pf-filter-tpl" className="stc-input !py-1.5">
            <option value="">All templates</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label>Type</Label>
          <select value={filterExt} onChange={(e) => setFilterExt(e.target.value)}
                  data-testid="pf-filter-ext" className="stc-input !py-1.5">
            <option value="">All</option>
            <option value="png">PNG</option>
            <option value="jpg">JPG</option>
          </select>
        </div>
        <button onClick={load} className="btn-outline text-[11px] flex items-center gap-1.5" data-testid="pf-refresh">
          <RefreshCw size={11} /> Refresh
        </button>
        <button onClick={downloadZipBlob} disabled={loading || files.length === 0} className="btn-gold text-[11px] flex items-center gap-1.5" data-testid="pf-dl-zip">
          <Download size={11} /> Download ZIP
        </button>
        <button onClick={removeAll} disabled={loading || files.length === 0} className="btn-outline border-red-700 text-red-400 hover:bg-red-900/20 text-[11px] flex items-center gap-1.5" data-testid="pf-del-all">
          <Trash2 size={11} /> Delete All
        </button>
      </div>

      {loading && <div className="text-[12px] text-[#8a857d] py-6 text-center">Loading…</div>}
      {!loading && files.length === 0 && (
        <div className="border border-dashed border-white/10 p-8 text-center text-[13px] text-[#8a857d]">
          No saved files{filterTpl || filterExt ? " matching this filter." : " yet."}
        </div>
      )}
      {!loading && files.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {files.map((f) => (
            <div key={f.id} className="bg-[#050505] border border-white/10 flex flex-col" data-testid={`pf-card-${f.id}`}>
              <div className="aspect-[4/5] bg-[#111] overflow-hidden flex items-center justify-center border-b border-white/5">
                {f.artwork_image ? (
                  <img src={abs(f.artwork_image)} alt={f.artwork_title || f.id}
                       loading="lazy" className="max-w-full max-h-full object-cover" />
                ) : (
                  <div className="text-[#8a857d] text-[10px] uppercase font-ui tracking-widest">No Image</div>
                )}
              </div>
              <div className="p-3 text-[11px] flex-1 flex flex-col gap-1.5">
                <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] line-clamp-1">{f.template_key} · {f.ext}</div>
                <div className="text-[#F4F1EA] font-display text-lg line-clamp-1">{f.artwork_title || <span className="text-[#8a857d] italic">No artwork</span>}</div>
                <div className="text-[#8a857d] text-xs">{human(f.size || 0)}</div>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5">
                  <a href={abs(f.url)} target="_blank" rel="noreferrer"
                     data-testid={`pf-view-${f.id}`}
                     className="text-[#8a857d] hover:text-[#C9A055] flex-1 text-center bg-white/5 hover:bg-white/10 py-1.5 text-[10px] flex items-center justify-center gap-1 transition-colors">
                    <Eye size={12} /> View
                  </a>
                  <button onClick={() => remove(f.id)}
                          data-testid={`pf-del-${f.id}`}
                          className="text-[#8a857d] hover:text-red-400 flex-1 text-center bg-white/5 hover:bg-white/10 py-1.5 text-[10px] flex items-center justify-center gap-1 transition-colors">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ tpl, onSaved }) {
  const [form, setForm] = useState(tpl);
  useEffect(() => { setForm(tpl); }, [tpl.key]); // eslint-disable-line
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    try {
      const { data } = await http.patch(`/admin/print/templates/${tpl.key}`, form);
      onSaved(data);
      toast.success("Template updated.");
    } catch (e) {
      toast.error("Update failed.");
    }
  };
  return (
    <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <NumField label="Canvas W" v={form.canvas_w} on={(v) => set("canvas_w", v)} />
        <NumField label="Canvas H" v={form.canvas_h} on={(v) => set("canvas_h", v)} />
        <NumField label="Border top" v={form.border_top} on={(v) => set("border_top", v)} />
        <NumField label="Border bottom" v={form.border_bottom} on={(v) => set("border_bottom", v)} />
        <NumField label="Border left" v={form.border_left} on={(v) => set("border_left", v)} />
        <NumField label="Border right" v={form.border_right} on={(v) => set("border_right", v)} />
        <NumField label="Logo height (px)" v={form.logo_h} on={(v) => set("logo_h", v)} />
        <NumField label="Logo width (px, 0=auto)" v={form.logo_w ?? 0} on={(v) => set("logo_w", v)} />
        <NumField label="Logo Y offset" v={form.logo_y_offset} on={(v) => set("logo_y_offset", v)} />
        <NumField label="JPG quality" v={form.jpg_quality || 95} on={(v) => set("jpg_quality", v)} />
      </div>
      <label className="block">
        <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Default scale mode</div>
        <select value={form.scale_mode || "fill"} onChange={(e) => set("scale_mode", e.target.value)}
                className="stc-input !py-1.5" data-testid={`tpl-mode-${tpl.key}`}>
          <option value="fill">FILL (cover, crop overflow)</option>
          <option value="fit">FIT (contain, no crop)</option>
        </select>
      </label>
      <button onClick={save} className="btn-outline text-[11px] w-full" data-testid={`tpl-save-${tpl.key}`}>
        <Save size={11} className="inline mr-1" /> Save template
      </button>
    </div>
  );
}

function NumField({ label, v, on }) {
  return (
    <label className="block">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">{label}</div>
      <input type="number" value={v} onChange={(e) => on(Number(e.target.value))} className="stc-input !py-1.5" />
    </label>
  );
}

function SliderRow({ label, value, min, max, step, suffix, onChange, testid }) {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between font-ui text-[10px] tracking-[0.22em] uppercase mb-1">
        <span className="text-[#C9A055]">{label}</span>
        <span className="text-[#8a857d]">{(typeof value === "number" ? value.toFixed(step < 0.1 ? 2 : 0) : value)}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
             onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}
             className="w-full accent-[#C9A055]" />
    </div>
  );
}

function CheckRow({ label, on, onChange, testid }) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="font-ui text-[11px] text-[#F4F1EA]">{label}</span>
      <button type="button" onClick={() => onChange(!on)} data-testid={testid}
              className={`w-9 h-5 rounded-full transition relative ${on ? "bg-[#C9A055]" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 ${on ? "left-4" : "left-0.5"} w-4 h-4 rounded-full bg-white transition-all`} />
      </button>
    </label>
  );
}

function ToggleChip({ label, color, on, onChange, testid }) {
  return (
    <button onClick={() => onChange(!on)} data-testid={testid}
            className={`px-2 py-1 border transition flex items-center gap-1.5 ${on ? "border-[#C9A055] text-[#F4F1EA]" : "border-white/15 text-[#8a857d]"}`}>
      <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </button>
  );
}

function Label({ children, className = "" }) {
  return (
    <div className={`font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-2 ${className}`}>
      {children}
    </div>
  );
}
